/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * MFASetupPage Component
 * ============================================================================
 *
 * MFA setup page with QR code display for TOTP authenticator apps.
 *
 * ============================================================================
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Copy, Check, AlertCircle, ShieldCheck } from 'lucide-react';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { CodeInput } from '@/components/ui/CodeInput';
import { Button } from '@/components/ui/Button';
import { useMfaSetup, useMfaSetupVerify } from '@/hooks/api/useAuth';
import { getErrorMessage, isApiError } from '@/services/api/errors';
import { MfaSetupResponse, UserRole } from '@/types/auth.types';
import { useAuthStore } from '@/stores/authStore';

function getDefaultRoute(role?: string): string {
  return role === 'SUPER_ADMIN' ? '/superadmin' : '/venta';
}

export function MFASetupPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const mfaSetupMutation = useMfaSetup();
  const mfaVerifyMutation = useMfaSetupVerify();

  const [setupData, setSetupData] = useState<MfaSetupResponse | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);

  // Fetch QR code on mount
  useEffect(() => {
    fetchMFASetupData();
  }, []);

  const fetchMFASetupData = () => {
    setError(null);
    mfaSetupMutation.mutate(undefined, {
      onSuccess: (data) => {
        setSetupData(data);
        if (data.recoveryCodes && data.recoveryCodes.length > 0) {
          setRecoveryCodes(data.recoveryCodes);
        }
      },
      onError: (err) => {
        if (isApiError(err)) {
          setError(err.message || getErrorMessage(err.code));
        } else {
          setError(t('mfaSetup.errors.setupFailed'));
        }
      },
    });
  };

  const handleCopySecret = async () => {
    if (!setupData) return;

    try {
      await navigator.clipboard.writeText(setupData.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyRecoveryCodes = async () => {
    if (!recoveryCodes) return;

    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleVerify = async (code: string) => {
    setError(null);

    mfaVerifyMutation.mutate(
      { code },
      {
        onSuccess: () => {
          // If there are recovery codes, show them before navigating
          if (recoveryCodes && recoveryCodes.length > 0) {
            setShowRecoveryCodes(true);
          } else {
            navigate(getDefaultRoute(user?.role as UserRole), {
              state: { mfaSetupComplete: true },
            });
          }
        },
        onError: (err) => {
          if (isApiError(err)) {
            setError(err.message || getErrorMessage(err.code));
          } else {
            setError(t('mfaSetup.errors.invalidCode'));
          }
          setVerificationCode('');
        },
      }
    );
  };

  const handleCodeComplete = (code: string) => {
    setVerificationCode(code);
    handleVerify(code);
  };

  const handleContinue = () => {
    navigate(getDefaultRoute(user?.role as UserRole), {
      state: { mfaSetupComplete: true },
    });
  };

  // Helper to format secret with spaces
  const formatSecret = (secret: string) => {
    return secret.match(/.{1,4}/g)?.join(' ') || secret;
  };

  // Show recovery codes after successful verification
  if (showRecoveryCodes && recoveryCodes) {
    return (
      <AuthLayout
        title={t('mfaSetup.recoveryCodes.title')}
        subtitle={t('mfaSetup.recoveryCodes.subtitle')}
        icon={<ShieldCheck size={28} />}
        maxWidth="md"
      >
        <div className="mfa-setup">
          <div className="recovery-codes-container">
            <div className="recovery-codes-grid">
              {recoveryCodes.map((code, index) => (
                <div key={index} className="recovery-code">
                  <span className="recovery-code-index">{index + 1}.</span>
                  <code>{code}</code>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="recovery-copy-btn"
              onClick={handleCopyRecoveryCodes}
            >
              {secretCopied ? <Check size={16} /> : <Copy size={16} />}
              {secretCopied ? t('mfaSetup.recoveryCodes.copied') : t('mfaSetup.recoveryCodes.copyAll')}
            </button>
          </div>

          <div className="recovery-warning">
            <AlertCircle size={18} />
            <p>
              {t('mfaSetup.recoveryCodes.warning')}
            </p>
          </div>

          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleContinue}
            glow
          >
            {t('mfaSetup.recoveryCodes.continue')}
          </Button>
        </div>

        <style>{`
          .recovery-codes-container {
            background: rgba(13, 15, 20, 0.6);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: var(--radius-lg);
            padding: 20px;
          }

          [data-theme="light"] .recovery-codes-container {
            background: #F7FAFC;
            border-color: #CBD5E0;
          }

          .recovery-codes-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-bottom: 16px;
          }

          .recovery-code {
            display: flex;
            align-items: center;
            gap: 8px;
            font-family: var(--font-mono);
            font-size: 14px;
          }

          .recovery-code-index {
            color: var(--color-text-muted);
            min-width: 24px;
          }

          .recovery-code code {
            color: var(--color-text-primary);
            font-weight: 500;
          }

          .recovery-copy-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            width: 100%;
            padding: 10px;
            background: transparent;
            border: 1px dashed rgba(255, 255, 255, 0.2);
            border-radius: var(--radius-md);
            color: var(--color-text-secondary);
            font-size: var(--text-sm);
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .recovery-copy-btn:hover {
            border-color: var(--color-brand-primary);
            color: var(--color-brand-primary);
          }

          [data-theme="light"] .recovery-copy-btn {
            border-color: rgba(0, 0, 0, 0.2);
          }

          .recovery-warning {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 14px 16px;
            background: rgba(234, 179, 8, 0.1);
            border: 1px solid rgba(234, 179, 8, 0.3);
            border-radius: var(--radius-lg);
            color: #EAB308;
          }

          .recovery-warning p {
            margin: 0;
            font-size: var(--text-sm);
            line-height: 1.5;
          }

          @media (max-width: 640px) {
            .recovery-codes-container {
              padding: 1rem;
            }
            .recovery-codes-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('mfaSetup.title')}
      subtitle={t('mfaSetup.subtitle')}
      icon={<Lock size={28} />}
      maxWidth="md"
    >
      <div className="mfa-setup">
        {mfaSetupMutation.isPending ? (
          <div className="mfa-loading">
            <div className="mfa-loading-spinner" />
            <p>{t('mfaSetup.generating')}</p>
          </div>
        ) : error && !setupData ? (
          <div className="mfa-error">
            <AlertCircle size={48} />
            <p>{error}</p>
            <Button variant="secondary" onClick={fetchMFASetupData}>
              {t('mfaSetup.tryAgain')}
            </Button>
          </div>
        ) : setupData ? (
          <>
            {/* QR Code */}
            <div className="mfa-qr-container">
              <div className="mfa-qr-wrapper">
                {setupData.qrCodeBase64 ? (
                  <img
                    src={`data:image/png;base64,${setupData.qrCodeBase64}`}
                    alt={t('mfaSetup.qrCodeAlt')}
                    className="mfa-qr-image"
                  />
                ) : (
                  <img
                    src={setupData.qrCodeUri}
                    alt={t('mfaSetup.qrCodeAlt')}
                    className="mfa-qr-image"
                  />
                )}
                <div className="mfa-qr-glow" />
              </div>
            </div>

            {/* Manual entry option */}
            <div className="mfa-manual">
              <p className="mfa-manual-label">{t('mfaSetup.manualEntry')}</p>
              <div className="mfa-secret-container">
                <code className="mfa-secret">{formatSecret(setupData.secret)}</code>
                <button
                  type="button"
                  className="mfa-copy-btn"
                  onClick={handleCopySecret}
                  title={t('mfaSetup.copyToClipboard')}
                >
                  {secretCopied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>

            {/* Verification section */}
            <div className="mfa-verify-section">
              <p className="mfa-verify-label">
                {t('mfaSetup.verifyLabel')}
              </p>

              {error && (
                <div className="mfa-verify-error">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              <CodeInput
                length={6}
                onComplete={handleCodeComplete}
                onChange={setVerificationCode}
                disabled={mfaVerifyMutation.isPending}
                error={!!error}
                autoFocus={false}
              />

              <Button
                type="button"
                variant="primary"
                size="lg"
                fullWidth
                loading={mfaVerifyMutation.isPending}
                loadingText={t('mfaSetup.verifying')}
                disabled={verificationCode.length !== 6}
                onClick={() => handleVerify(verificationCode)}
                glow
              >
                {t('mfaSetup.verifyButton')}
              </Button>
            </div>
          </>
        ) : null}
      </div>

      <style>{`
        .mfa-setup {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .mfa-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 40px 0;
          color: var(--color-text-secondary);
        }

        .mfa-loading-spinner {
          width: 48px;
          height: 48px;
          border: 3px solid var(--color-border-default);
          border-top-color: var(--color-brand-primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .mfa-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 40px 0;
          color: var(--color-error);
          text-align: center;
        }

        .mfa-qr-container {
          display: flex;
          justify-content: center;
          padding: 16px 0;
        }

        .mfa-qr-wrapper {
          position: relative;
          padding: 16px;
          background: white;
          border-radius: var(--radius-xl);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
        }

        .mfa-qr-image {
          display: block;
          width: 160px;
          height: 160px;
        }

        .mfa-qr-glow {
          position: absolute;
          inset: -2px;
          border-radius: var(--radius-xl);
          background: linear-gradient(
            135deg,
            var(--color-brand-primary) 0%,
            transparent 40%,
            transparent 60%,
            var(--color-brand-primary) 100%
          );
          background-size: 200% 200%;
          animation: glowMove 3s linear infinite;
          z-index: -1;
        }

        @keyframes glowMove {
          0% { background-position: 0% 0%; }
          50% { background-position: 100% 100%; }
          100% { background-position: 0% 0%; }
        }

        .mfa-manual {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .mfa-manual-label {
          font-size: 11px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #64748B;
          margin: 0;
        }

        .mfa-secret-container {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 20px;
          background: rgba(13, 15, 20, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-lg);
        }

        [data-theme="light"] .mfa-secret-container {
          background: #F7FAFC;
          border-color: #CBD5E0;
        }

        .mfa-secret {
          font-family: var(--font-mono);
          font-size: 16px;
          font-weight: 600;
          color: var(--color-text-primary);
          letter-spacing: 3px;
        }

        .mfa-copy-btn {
          padding: 6px;
          background: transparent;
          border: none;
          border-radius: var(--radius-sm);
          color: var(--color-text-muted);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .mfa-copy-btn:hover {
          color: var(--color-text-primary);
        }

        .mfa-verify-section {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .mfa-verify-label {
          font-size: var(--text-sm);
          color: var(--color-text-secondary);
          text-align: center;
          margin: 0;
        }

        .mfa-verify-error {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: var(--color-error-light);
          border: 1px solid var(--color-error);
          border-radius: var(--radius-md);
          color: var(--color-error);
          font-size: var(--text-sm);
        }

        @media (max-width: 640px) {
          .mfa-setup {
            gap: 1.25rem;
          }
          .mfa-secret-container {
            padding: 10px 12px;
          }
          .mfa-secret {
            font-size: 13px;
            letter-spacing: 1px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .mfa-loading-spinner { animation: none !important; }
          .mfa-qr-glow { animation: none !important; }
        }
      `}</style>
    </AuthLayout>
  );
}

export default MFASetupPage;
